// src/components/communications/MessageFormModal.js
import React, { useState, useEffect } from 'react';
import { XMarkIcon, MegaphoneIcon, CalendarDaysIcon, UserGroupIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

const MessageFormModal = ({ isOpen, onClose, message, onSave, availableTypes, availableAudiences }) => {
    const isEditing = !!message;
    const [formData, setFormData] = useState({
        name: '',
        type: availableTypes[0] || 'Email', // Default to Email
        subject: '',
        audience: availableAudiences[0] || '',
        content: '',
        scheduledDate: '',
        sendNow: true, // Default to send now
    });

    useEffect(() => {
        if (message) {
            setFormData({
                name: message.name || '',
                type: message.type || 'Email',
                subject: message.subject || '',
                audience: message.audience || availableAudiences[0] || '',
                content: message.content || '',
                scheduledDate: message.scheduledDate || '',
                sendNow: !message.scheduledDate && message.status !== 'Draft', // If it was sent immediately, default to sendNow
            });
        } else {
            setFormData({
                name: '',
                type: availableTypes[0] || 'Email',
                subject: '',
                audience: availableAudiences[0] || '',
                content: '',
                scheduledDate: '',
                sendNow: true,
            });
        }
    }, [message, isOpen, availableTypes, availableAudiences]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Simple validation
        if (!formData.name || !formData.type || !formData.audience || !formData.content) {
            alert('Please fill in all required fields: Message Name, Type, Audience, and Content.');
            return;
        }
        if (formData.type === 'Email' && !formData.subject) {
            alert('Email messages require a Subject.');
            return;
        }
        if (!formData.sendNow && !formData.scheduledDate) {
            alert('Please select a Scheduled Date or choose to Send Now.');
            return;
        }

        const dataToSave = {
            ...formData,
            id: message ? message.id : undefined,
            // Adjust status based on sendNow/scheduledDate in parent component (CommunicationsPage)
        };
        onSave(dataToSave);
    };

    const modalTitle = isEditing ? 'Edit Message' : 'Create New Message';
    const submitButtonText = isEditing ? 'Save Changes' : (formData.sendNow ? 'Send Message Now' : 'Schedule Message');

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                {/* Modal Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-800">{modalTitle}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                {/* Modal Body */}
                <form onSubmit={handleSubmit} className="p-6">
                    <div className="grid grid-cols-1 gap-y-4">
                        {/* Message Name (Internal) */}
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Message Name <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                name="name"
                                id="name"
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full border-gray-300 rounded-lg shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                placeholder="e.g., Welcome Email, Points Reminder SMS"
                                required
                            />
                            <p className="mt-2 text-xs text-gray-500">This name is for your internal reference only.</p>
                        </div>

                        {/* Message Type */}
                        <div>
                            <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">Message Type <span className="text-red-500">*</span></label>
                            <select
                                name="type"
                                id="type"
                                value={formData.type}
                                onChange={handleChange}
                                className="w-full border-gray-300 rounded-lg shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                required
                            >
                                {availableTypes.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>

                        {/* Subject (for Email only) */}
                        {formData.type === 'Email' && (
                            <div>
                                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">Subject <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    name="subject"
                                    id="subject"
                                    value={formData.subject}
                                    onChange={handleChange}
                                    className="w-full border-gray-300 rounded-lg shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                    placeholder="e.g., Your Rewards Are Waiting!"
                                    required={formData.type === 'Email'}
                                />
                            </div>
                        )}

                        {/* Audience */}
                        <div>
                            <label htmlFor="audience" className="block text-sm font-medium text-gray-700 mb-1">Target Audience <span className="text-red-500">*</span></label>
                            <select
                                name="audience"
                                id="audience"
                                value={formData.audience}
                                onChange={handleChange}
                                className="w-full border-gray-300 rounded-lg shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                required
                            >
                                {availableAudiences.map(audience => (
                                    <option key={audience} value={audience}>{audience}</option>
                                ))}
                            </select>
                            <p className="mt-2 text-xs text-gray-500 flex items-center">
                                <InformationCircleIcon className="h-4 w-4 mr-1" />
                                Who will receive this message.
                            </p>
                        </div>

                        {/* Message Content */}
                        <div>
                            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">Message Content <span className="text-red-500">*</span></label>
                            <textarea
                                name="content"
                                id="content"
                                rows="6"
                                value={formData.content}
                                onChange={handleChange}
                                className="w-full border-gray-300 rounded-lg shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                placeholder={`Write your message here. Use personalization tokens like {{customer_name}}, {{points_balance}} for dynamic content.`}
                                required
                            ></textarea>
                            <p className="mt-2 text-xs text-gray-500">
                                Max characters for SMS: 160.
                            </p>
                        </div>

                        {/* Scheduling Options */}
                        <div className="flex items-center space-x-4 mt-4">
                            <div className="flex items-center">
                                <input
                                    id="sendNow"
                                    name="sendNow"
                                    type="checkbox"
                                    checked={formData.sendNow}
                                    onChange={handleChange}
                                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                />
                                <label htmlFor="sendNow" className="ml-2 block text-sm text-gray-900">Send Now</label>
                            </div>
                            {!formData.sendNow && (
                                <div className="flex items-center space-x-2 flex-grow">
                                    <CalendarDaysIcon className="h-5 w-5 text-gray-500" />
                                    <label htmlFor="scheduledDate" className="sr-only">Schedule Date</label>
                                    <input
                                        type="datetime-local" // Allows date and time
                                        name="scheduledDate"
                                        id="scheduledDate"
                                        value={formData.scheduledDate}
                                        onChange={handleChange}
                                        className="flex-grow border-gray-300 rounded-lg shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        required={!formData.sendNow}
                                    />
                                </div>
                            )}
                        </div>

                    </div>

                    {/* Modal Actions */}
                    <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors shadow-md"
                        >
                            {submitButtonText}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default MessageFormModal;